/**
 * Blog Listing Page - WoodAlps Content Hub
 * 
 * This page displays all blog posts in a responsive grid layout with:
 * - Dynamic content loading from Sanity CMS
 * - Blog post cards with images, excerpts, and metadata
 * - Date formatting and author information
 * - Responsive grid layout for all screen sizes
 * - SEO-optimized individual post links
 * - Font customization support from CMS
 * 
 * Features:
 * - Server-side rendering for SEO benefits
 * - Optimized images with Next.js Image component
 * - Dynamic routing to individual blog posts
 * - Fallback content for missing images
 * - Professional typography with custom fonts
 * - Mobile-responsive card design
 * 
 * Content Management:
 * - Blog posts managed through Sanity CMS
 * - Support for rich content and media
 * - Publication date and author tracking
 * - SEO meta data optimization
 * 
 * @description Blog listing page with responsive post grid and CMS integration
 */

import { useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import {getBlogPosts, getBlogSettings } from '../src/app/studio/lib/sanity';
import { urlForImage } from '../src/app/studio/lib/image';
import { sanityLoader } from '../utils/sanityLoader';
import Layout from '../components/layout/Layout';
import { format } from 'date-fns';
import { useTranslation } from '../src/hooks/useTranslation';

/**
 * Blog Post Card Component
 * Renders individual blog post previews in the grid layout
 */
const BlogPostCard = ({ post }) => {
  const { t } = useTranslation();
  const { title, slug, publishedAt, mainImage, excerpt, titleFont = 'font-neuropol', authorName } = post;
  
  // Use the font class directly from Sanity, making sure it has the right format
  const fontClass = titleFont && titleFont.startsWith('font-') 
    ? titleFont 
    : `font-${titleFont || 'neuropol'}`;
  
  const formattedDate = publishedAt 
    ? format(new Date(publishedAt), 'MMMM d, yyyy')
    : 'Publication date not available';

  // Ensure the slug doesn't start with a slash to prevent double slash errors
  const postUrl = `/blog/${slug.current?.startsWith('/') ? slug.current.substring(1) : slug.current}`;

  // Get the appropriate alt text based on the current locale
  const { locale } = useTranslation();
  const currentLocale = locale || 'en';
  const getAltText = () => {
    if (mainImage) {
      const altKey = `alt${currentLocale.toUpperCase()}`;
      return mainImage[altKey] || mainImage.altEN || title || "Blog post image";
    }
    return "Blog post image";
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-wood-brown/20 hover:shadow-xl transition-all duration-300 h-full">
      <Link href={postUrl} className="block h-full flex flex-col">
        <div className="relative aspect-[16/9] w-full">
          {mainImage ? (
            <Image
              src={urlForImage(mainImage)}
              alt={getAltText()}
              fill
              loader={sanityLoader}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              className="object-cover"
            />
          ) : (
            <div className="bg-wood-brown/10 w-full h-full flex items-center justify-center">
              <span className="text-wood-brown/50 font-neuropol">WOODALPS</span>
            </div>
          )}
        </div>
        
        <div className="p-4 flex-1 flex flex-col">
          <h3 className={`text-lg ${fontClass} text-accent-color mb-2 line-clamp-2`}>
            {title}
          </h3>
          
          <div className="text-xs text-gray-500 mb-2 flex flex-wrap items-center">
            <span>{formattedDate}</span>
            {authorName && (
              <>
                <span className="mx-1">•</span>
                <span>{t('pages.blog_by')} {authorName}</span>
              </>
            )}
          </div>
          
          {excerpt && (
            <div className="text-sm text-gray-700 mb-3 line-clamp-2 flex-1">
              {excerpt}
            </div>
          )}
          
          <div className="mt-auto">
            <span className="inline-flex items-center font-bold text-accent-color text-sm hover:underline">
              {t('pages.blog_read_more')}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default function Blog({ posts, blogSettings }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const { t } = useTranslation();
  // Filter posts based on search term
  const filteredPosts = posts.filter(post => {
    const matchesSearch = searchTerm === '' || 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (post.excerpt && post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  return (
    <>
      <Head>
        <title>{`${blogSettings?.title || 'Blog'} | WOODALPS`}</title>
        <meta
          name="description"
          content={blogSettings?.description || "Explore articles about wooden bikes, craftsmanship, and sustainable biking by WoodAlps."}
        />
      </Head>

      {/* Hero Banner */}
      <section className="relative h-[50vh] sm:h-[60vh] md:h-[70vh] bg-deep-black  w-full ">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src={blogSettings?.heroImage ? urlForImage(blogSettings.heroImage) : "/images/blog-hero.jpg"}
            alt={blogSettings?.heroImage?.alt || "WoodAlps Blog"}
            fill
            loader={sanityLoader}
            sizes="100vw"
            className="object-cover"
            priority
          />
        </div>
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>

        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center px-4 sm:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-2 sm:mb-4 font-neuropol">
            {blogSettings?.title || 'Our Blog'}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white max-w-2xl">
            {blogSettings?.description || 'Stories and insights about our wooden bikes, craftsmanship, and sustainable biking adventures'}
          </p>
        </div>
      </section>

      {/* Blog Content Section */}
      <section className="py-10 sm:py-16 md:py-24 bg-gradient-to-b from-white to-wood-brown/5">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Filters and Search */}
          <div className="mb-8 sm:mb-12 flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4">
            {/* Categories/Filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveFilter('all')}
                className="px-4 py-2 rounded-md bg-accent-color text-white font-medium hover:bg-accent-color/90 active:bg-accent-color/80 transition-colors"
              >
                {t('pages.blog_filter_all')}
              </button>
            </div>
            
            {/* Search */}
            <div className="w-full sm:w-auto sm:max-w-xs">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('pages.blog_filter_search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pr-10 rounded-md border border-wood-brown/20 focus:outline-none focus:ring-2 focus:ring-accent-color"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Posts Grid - centered with 2 per line */}
          {filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {filteredPosts.map((post) => (
                <BlogPostCard key={post._id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <h3 className="text-2xl font-neuropol text-wood-brown mb-4">{t('pages.blog_no_posts_found')}</h3>
              <p className="text-gray-600">
                {t('pages.blog_try_changing_search_criteria') || 'Try changing your search criteria or check back later for new content.'}
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

// Use Layout component as the page layout
Blog.getLayout = function getLayout(page) {
  return <Layout transparentHeader={true} headerShadow={true}>{page}</Layout>;
};

export async function getStaticProps({ preview = false, locale = 'en' }) {
  const posts = await getBlogPosts(preview, locale);
  const blogSettings = await getBlogSettings(preview, locale);
  
  return {
    props: {
      posts,
      blogSettings,
    },
    // Regenerate on every request in preview, otherwise once per day
    revalidate: process.env.VERCEL_ENV === 'preview' ? 1 : 86400,
  };
}
